import { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ApiResponse } from 'src/api';
import { useSession } from 'src/stores/session';
import { api, PaginatedResponse } from 'src/service/api';
import { api as axios } from 'boot/axios';
import { throttlePromises } from 'src/service/ThrottlePromises';
import { useNotifier } from 'src/compositions/UseNotifier';
import { FeatureFlags } from 'src/service/FeatureFlags';

const { notifyError } = useNotifier();

// user defined type guard to check for an RFC7807 error message
function isApiResponse(object: unknown): object is ApiResponse {
  return (
    !!object &&
    Object.prototype.hasOwnProperty.call(object, 'status') &&
    Object.prototype.hasOwnProperty.call(object, 'title')
    // && Object.prototype.hasOwnProperty.call(object, 'detail')
  );
}

// an exception thrown on the API that did not get properly formatted to an ApiResponse
export interface ApiExceptionResponse {
  error: {
    message?: string; // human friendly text
    code?: string; // a camel case machine friendly name eg the exception class name
    statusCode?: number;
    // there may be other fields but we don't display them to the user
  };
}

function isExceptionResponse(object: unknown): object is ApiExceptionResponse {
  return !!object && Object.prototype.hasOwnProperty.call(object, 'error');
}

function setBearerToken(
  config: InternalAxiosRequestConfig,
  token: string,
): InternalAxiosRequestConfig {
   
  config.headers['Authorization'] = `Bearer ${token}`;
  return config;
}

export function addBearerToken(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  const session = useSession();
  if (api.requiresAccessToken(config.url) && session.accessToken) {
    return setBearerToken(config, session.accessToken);
  }
  if (api.requiresRefreshToken(config.url) && session.refreshToken) {
    return setBearerToken(config, session.refreshToken);
  }
  return config;
}

export const ImpersonationHeader = 'X-Wr-Impersonate-User';

// if admin user is logged in as someone else then add a cookie that tells the api to impersonate that user id
export function addImpersonationHeader(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  const session = useSession();
  if (session.isImpersonating) {
    config.headers[ImpersonationHeader] = session.userId;
  }
  return config;
}

export function displayErrorMessage(error: AxiosError): Promise<AxiosError> {
  const response = error.response?.data;
  if (isApiResponse(response)) {
    notifyError(
      response?.title ??
        'The API failed in some mysterious way. Please insert coin',
    );
  } else if (isExceptionResponse(response)) {
    notifyError(
      response?.error?.message ??
        'The API failed in some mysterious way. Please insert coin',
    );
  } else {
    notifyError(
      error.message ??
        'There was a problem calling the API: maybe it timed out?',
    );
  }
  /* istanbul ignore next */
  if (!FeatureFlags.isUnitTest()) console.error(error); // Log caught errors to the console
  return Promise.resolve(error); // reject() would throw: we want to handle them
}

export async function refreshAccessToken(error: AxiosError): Promise<unknown> {
  // see https://stackoverflow.com/questions/68714143/how-can-i-use-axios-interceptors-to-add-some-headers-to-responses
  if (error.response?.status !== 401) {
    // 401 is a "please log in" message from the API: ignore everything else
    return Promise.reject(error);
  }

  if (api.isAuthUrl(error.config?.url)) {
    // Avoid infinite loop: trying to log in but the API is telling us we can't.
    // Also catches the case where /auth/refresh trying to get a new token fails
    return Promise.reject(error);
  }

  const session = useSession();
  if (!session.isLoggedIn) {
    // not logged in: the ui should handle the redirect for us, and displayErrorMessage() should display it...
    notifyError('Your session has expired. Please log in again');
    return Promise.resolve(error);
  }

  const originalRequest = error.config;
  const token = await api.refreshToken();
  if (token) {
    session.login(token);
    if (
      session.isLoggedIn &&
      session.accessTokenIsValid &&
      session.refreshTokenIsValid &&
      !!originalRequest
    ) {
      return axios(setBearerToken(originalRequest, session.accessToken ?? ''));
    }
    // notifyError('A server problem has occurred. Please log in again');
    // the token is corrupted somehow: our session is broken likely because the api returned an invalid token.
    // Prevent infinite recursion by logging out
  }
  // else /auth/refresh failed so just abort with the original error. The auth error should get displayed by displayErrorMessage()
  session.logout();
  return Promise.reject(error);
}

// get all the pages of a paginated query
export async function getItemsOnAllPages<T>(
  callApi: (page: number) => Promise<PaginatedResponse<T> | undefined>,
  acceptRow: (item: T) => void,
  concurrency = 5,
) {
  const firstPage = await callApi(1);
  if (!firstPage?.data) return;
  firstPage.data.forEach((item) => acceptRow(item));

  // get pages 2..n in parallel
  function* nextPage(nPages: number) {
    for (let pageNumber = 2; pageNumber <= nPages; pageNumber++) {
      yield callApi(pageNumber).then(
        (page) => page?.data?.forEach((item) => acceptRow(item)),
      );
    }
  }

  await throttlePromises(
    concurrency,
    nextPage(firstPage.paginator?.pageCount ?? 0),
  );
}
